import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, FormControl, FormLabel, Input, Select, Button, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FinanceToolsStampDuty: React.FC = () => {
  const [propertyValue, setPropertyValue] = useState(650000);
  const [state, setState] = useState("VIC");
  const [firstHomeBuyer, setFirstHomeBuyer] = useState("no");

  const calculateStampDuty = () => {
    let duty = 0;
    const value = propertyValue;

    if (state === "VIC") {
      if (value <= 25000) duty = value * 0.014;
      else if (value <= 130000) duty = 350 + (value - 25000) * 0.024;
      else if (value <= 960000) duty = 2870 + (value - 130000) * 0.06;
      else duty = 52670 + (value - 960000) * 0.055;

      if (firstHomeBuyer === "yes" && value <= 600000) duty = 0;
      else if (firstHomeBuyer === "yes" && value <= 750000) duty *= 0.5;
    } else if (state === "NSW") {
      if (value <= 14000) duty = value * 0.0125;
      else if (value <= 32000) duty = 175 + (value - 14000) * 0.015;
      else if (value <= 87000) duty = 445 + (value - 32000) * 0.0165;
      else if (value <= 327000) duty = 1353 + (value - 87000) * 0.035;
      else if (value <= 1064000) duty = 9753 + (value - 327000) * 0.045;
      else duty = 42958 + (value - 1064000) * 0.055;

      if (firstHomeBuyer === "yes" && value <= 650000) duty = 0;
      else if (firstHomeBuyer === "yes" && value <= 800000) duty *= 0.75;
    } else if (state === "QLD") {
      if (value <= 5000) duty = 0;
      else if (value <= 75000) duty = (value - 5000) * 0.015;
      else if (value <= 540000) duty = 1050 + (value - 75000) * 0.035;
      else if (value <= 1000000) duty = 17325 + (value - 540000) * 0.045;
      else duty = 38025 + (value - 1000000) * 0.0575;

      if (firstHomeBuyer === "yes" && value <= 550000) duty = 0;
    }

    return Math.round(duty);
  };

  const stampDuty = calculateStampDuty();
  const transferFee = 150;
  const mortgageFee = 150;
  const totalCosts = stampDuty + transferFee + mortgageFee;

  return (
    <Box minH="100vh" bg="white">
      <SEOHead pagePath="/finance/tools/stamp-duty" fallbackTitle="Stamp Duty Calculator" fallbackDescription="Calculate stamp duty for property purchases" />
      <NavbarWithCallToAction />
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">Finance Section</Text>
            <Heading size="2xl" fontWeight="bold">Stamp Duty Calculator</Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Calculate stamp duty costs</Text>
          </VStack>
        </Container>
      </Box>
      <Container maxW="container.xl" py={20} flex="1">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10}>
          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="lg">
            <Heading size="md" mb={6}>Property Details</Heading>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Property Value</FormLabel>
                <Input type="number" value={propertyValue} onChange={(e) => setPropertyValue(Number(e.target.value))} size="lg" />
                <Text fontSize="sm" color="gray.600" mt={1}>${propertyValue.toLocaleString()}</Text>
              </FormControl>
              <FormControl>
                <FormLabel>State/Territory</FormLabel>
                <Select value={state} onChange={(e) => setState(e.target.value)} size="lg">
                  <option value="VIC">Victoria</option>
                  <option value="NSW">New South Wales</option>
                  <option value="QLD">Queensland</option>
                  <option value="SA">South Australia</option>
                  <option value="WA">Western Australia</option>
                  <option value="TAS">Tasmania</option>
                  <option value="NT">Northern Territory</option>
                  <option value="ACT">ACT</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>First Home Buyer?</FormLabel>
                <Select value={firstHomeBuyer} onChange={(e) => setFirstHomeBuyer(e.target.value)} size="lg">
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </Select>
              </FormControl>
              <Button colorScheme="orange" size="lg" onClick={() => calculateStampDuty()}>Calculate Costs</Button>
            </VStack>
          </Box>
          <Box>
            <Box bg="gradient-to-br" bgGradient="linear(to-br, orange.500, red.600)" p={8} borderRadius="xl" color="white" textAlign="center" mb={6}>
              <Text fontSize="sm" textTransform="uppercase" mb={2}>Estimated Stamp Duty</Text>
              <Heading size="3xl" mb={2}>${stampDuty.toLocaleString()}</Heading>
              <Text fontSize="sm">For {state} property purchase</Text>
            </Box>
            <VStack spacing={4} mb={6}>
              <Stat bg="orange.50" p={4} borderRadius="lg" w="full">
                <StatLabel>Stamp Duty</StatLabel>
                <StatNumber>${stampDuty.toLocaleString()}</StatNumber>
                <StatHelpText>{state} rates</StatHelpText>
              </Stat>
              <Stat bg="blue.50" p={4} borderRadius="lg" w="full">
                <StatLabel>Transfer Fee</StatLabel>
                <StatNumber>${transferFee}</StatNumber>
                <StatHelpText>Land title transfer</StatHelpText>
              </Stat>
              <Stat bg="purple.50" p={4} borderRadius="lg" w="full">
                <StatLabel>Mortgage Registration</StatLabel>
                <StatNumber>${mortgageFee}</StatNumber>
                <StatHelpText>Approx. fee</StatHelpText>
              </Stat>
              <Stat bg="green.50" p={4} borderRadius="lg" w="full">
                <StatLabel>Total Settlement Costs</StatLabel>
                <StatNumber fontSize="2xl">${totalCosts.toLocaleString()}</StatNumber>
                <StatHelpText>Estimated</StatHelpText>
              </Stat>
            </VStack>
            <Box bg="yellow.50" p={6} borderRadius="lg" border="1px solid" borderColor="yellow.200">
              <Heading size="sm" mb={3}>Important Note</Heading>
              <Text fontSize="sm">This is an estimate only. Actual stamp duty varies by state and concessions available. First home buyer exemptions and concessions apply in most states up to certain thresholds. Consult with a conveyancer for exact costs.</Text>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>
      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceToolsStampDuty;
